import type { JSX, SVGProps } from 'react';

/**
 * The landing page's OWN icon set — a small, cohesive family of inline-SVG line icons in the brand's
 * amber/gold visual language. Replaces every stock OS emoji that was standing in as an icon (Features,
 * How-it-works), which read as cheap and inconsistent across platforms. Hand-tuned so the whole set shares
 * ONE grammar: a 24×24 viewbox, ~1.8px rounded strokes, `currentColor` (so a parent can tint them amber),
 * gentle soft-fill accents where a glyph needs a focal point.
 *
 * ACCESSIBILITY: every icon is purely DECORATIVE next to a real text label, so each sets `aria-hidden` and
 * is never in the a11y tree (the card's `<h3>`/copy carries the meaning). They forward `className`/props so
 * a consumer controls size + colour from the outside (no hard-coded dimensions fighting the layout).
 *
 * PERFORMANCE: pure stateless SVG — no Pixi, no animation, no allocation. They render once with the card.
 */

/** Shared defaults: inherit colour, round joins, no fill unless a glyph opts in. The 24-grid is the unit. */
type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...props }: IconProps & { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="h-6 w-6"
      {...props}
    >
      {children}
    </svg>
  );
}

/**
 * Spatial voice — a speaker emitting POSITIVE sound waves (the crossed-out 🔇 we replaced wrongly read as
 * MUTED, the exact opposite of the feature). Three rising arcs = sound carrying through space.
 */
export function IconVoice(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 9v6h3l5 4V5L7 9H4Z" fill="currentColor" fillOpacity={0.16} />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18 6a8.5 8.5 0 0 1 0 12" />
    </Svg>
  );
}

/** Screen share — a monitor with an upward "broadcasting" arrow, the share-to-the-room read. */
export function IconScreen(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="12" rx="2" fill="currentColor" fillOpacity={0.12} />
      <path d="M9 20h6M12 16v4" />
      <path d="M12 12V7m0 0-2.2 2.2M12 7l2.2 2.2" />
    </Svg>
  );
}

/** Show your face — a friendly avatar inside a camera-bubble ring (the proximity webcam porthole). */
export function IconFace(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" fill="currentColor" fillOpacity={0.1} />
      <circle cx="12" cy="10" r="2.6" />
      <path d="M7.5 17a4.8 4.8 0 0 1 9 0" />
    </Svg>
  );
}

/** Real collaboration — two figures leaning together (a huddle), the "gather round" read. */
export function IconCollab(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="8.5" cy="8" r="2.6" fill="currentColor" fillOpacity={0.14} />
      <circle cx="15.5" cy="8" r="2.6" fill="currentColor" fillOpacity={0.14} />
      <path d="M4 18a4.5 4.5 0 0 1 8.5-2" />
      <path d="M11.5 16a4.5 4.5 0 0 1 8.5 2" />
    </Svg>
  );
}

/** Many rooms — a little floorplan of connected rooms with a doorway between them. */
export function IconRooms(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" fill="currentColor" fillOpacity={0.1} />
      <path d="M12 4v6M12 14v6" />
      <path d="M3 12h6M15 12h6" />
    </Svg>
  );
}

/** Custom characters — a duck-ish critter head (beak + eye), the "be exactly you" read. */
export function IconCharacter(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        d="M7 14a5 5 0 1 1 9.5-2.2L20 12l-3 1.4A5 5 0 0 1 7 14Z"
        fill="currentColor"
        fillOpacity={0.14}
      />
      <circle cx="10.5" cy="11" r="1" fill="currentColor" stroke="none" />
      <path d="M9 18.5c1.2.6 3.8.6 5 0" />
    </Svg>
  );
}

/** Customisable spaces — a paint/theme swatch with a brush stroke, the "make it yours" read. */
export function IconTheme(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="4" y="4" width="12" height="12" rx="2" fill="currentColor" fillOpacity={0.12} />
      <path d="M9 11.5 12 9l3 3" />
      <path d="M14 14.5c0 2.5 2 4.5 4.5 4.5s2-2.2 0-3.5" />
    </Svg>
  );
}

/** Moments together — a celebratory spark/confetti burst (the "world that celebrates" read). */
export function IconSparkle(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        d="M12 3.5c.6 3.5 1.4 4.4 4.9 5-3.5.6-4.3 1.5-4.9 5-.6-3.5-1.4-4.4-4.9-5 3.5-.6 4.3-1.5 4.9-5Z"
        fill="currentColor"
        fillOpacity={0.16}
      />
      <path d="M18.5 14.5c.3 1.6.7 2 2.3 2.3-1.6.3-2 .7-2.3 2.3-.3-1.6-.7-2-2.3-2.3 1.6-.3 2-.7 2.3-2.3Z" />
    </Svg>
  );
}

/** Watch together — a play ▶ inside a soft screen tile (queue a video, the room watches in sync). */
export function IconWatch(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="13" rx="2" fill="currentColor" fillOpacity={0.12} />
      <path d="M10.5 9.5 14.5 12l-4 2.5V9.5Z" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Play together — a smiling face with a little spark (emotes, reactions, sounds, quick games). */
export function IconPlay(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <circle cx="11" cy="12" r="7.5" fill="currentColor" fillOpacity={0.12} />
      <circle cx="8.6" cy="10.6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="13.4" cy="10.6" r="0.9" fill="currentColor" stroke="none" />
      <path d="M8.5 13.6a4 4 0 0 0 5 0" />
      <path d="M18.5 4.5c.2 1.3.5 1.6 1.8 1.8-1.3.2-1.6.5-1.8 1.8-.2-1.3-.5-1.6-1.8-1.8 1.3-.2 1.6-.5 1.8-1.8Z" />
    </Svg>
  );
}

/**
 * Earned cosmetics & pets — a little CROWN (unlocked finery, status earned not bought). Deliberately a
 * crown, not the party-hat triangle we tried first: a bare triangle-on-a-line read as a ⚠️ warning sign.
 */
export function IconCosmetics(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        d="M5.5 17 4.5 8.5 8.5 12 12 6.5 15.5 12l4-3.5L18.5 17H5.5Z"
        fill="currentColor"
        fillOpacity={0.16}
      />
      <path d="M7 20h10" />
      <circle cx="4.5" cy="8.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="6.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="19.5" cy="8.5" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Reputation — a heart held in cupped hands (recognition GIVEN by a peer, the most valuable currency). */
export function IconReputation(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        d="M12 6.6c1.1-1.6 3.6-1.6 4.5.1.8 1.5-.3 3.2-1.6 4.3L12 14.5 9.1 11c-1.3-1.1-2.4-2.8-1.6-4.3.9-1.7 3.4-1.7 4.5-.1Z"
        fill="currentColor"
        fillOpacity={0.16}
      />
      <path d="M4 15c1.5-1 3-1 4.4.2L12 18l3.6-2.8c1.4-1.2 2.9-1.2 4.4-.2" />
      <path d="M5.5 18.5 9 21M18.5 18.5 15 21" />
    </Svg>
  );
}

/** Your company is a guild — a banner/shield over two figures (a whole team levelling up together). */
export function IconGuild(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M12 3 5.5 5.2v5.3c0 3.6 2.6 6 6.5 7.2 3.9-1.2 6.5-3.6 6.5-7.2V5.2L12 3Z" fill="currentColor" fillOpacity={0.14} />
      <circle cx="9.5" cy="9" r="1.6" />
      <circle cx="14.5" cy="9" r="1.6" />
      <path d="M7.2 14a3 3 0 0 1 9.6 0" />
    </Svg>
  );
}

/** Every release is a party — a trophy mid-confetti (ship something, the whole guild feels the moment). */
export function IconParty(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M8 4h8v3a4 4 0 0 1-8 0V4Z" fill="currentColor" fillOpacity={0.16} />
      <path d="M8 5H5.5a2.5 2.5 0 0 0 3 3M16 5h2.5a2.5 2.5 0 0 1-3 3" />
      <path d="M12 11v3m-2.5 5h5m-4-2.5h3" />
      <path d="M4.5 12.5 3.5 11M19.5 12.5l1-1.5M5 16l-1.3.4M19 16l1.3.4" stroke="currentColor" strokeOpacity={0.7} />
    </Svg>
  );
}

// ── How-it-works step icons (the 🏠🔗🐤🚀 we replaced) ──────────────────────────────────────────────

/** Create a room — a cosy house/door (your world). */
export function IconRoom(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M4 11 12 4l8 7" />
      <path d="M6 10v9h12v-9" fill="currentColor" fillOpacity={0.1} />
      <path d="M10 19v-5h4v5" />
    </Svg>
  );
}

/** Share the link — two link rings (the door is a link). */
export function IconLink(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 15a3 3 0 0 1 0-4l2-2a3 3 0 0 1 4 4l-1 1" />
      <path d="M15 9a3 3 0 0 1 0 4l-2 2a3 3 0 0 1-4-4l1-1" />
    </Svg>
  );
}

/** Friends join — a little critter walking through a doorway (the guest tapping in). */
export function IconJoin(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" fill="currentColor" fillOpacity={0.1} />
      <path d="M11 12H4m0 0 3-3m-3 3 3 3" />
    </Svg>
  );
}

/** Map editor — a folded map glyph (the level-authoring tool's secondary CTA in the hero). */
export function IconMap(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path d="M9 4 4 6v14l5-2 6 2 5-2V4l-5 2-6-2Z" fill="currentColor" fillOpacity={0.1} />
      <path d="M9 4v14M15 6v14" />
    </Svg>
  );
}

/** Work together — an upward spark/rocket of momentum (the day takes off). */
export function IconLaunch(props: IconProps): JSX.Element {
  return (
    <Svg {...props}>
      <path
        d="M12 3c3 1.5 4.5 4.5 4.5 8 0 2-.7 3.6-1.5 4.5h-6C8.2 14.6 7.5 13 7.5 11 7.5 7.5 9 4.5 12 3Z"
        fill="currentColor"
        fillOpacity={0.14}
      />
      <circle cx="12" cy="10" r="1.6" />
      <path d="M9 17l-1.5 3M15 17l1.5 3M12 17v3" />
    </Svg>
  );
}
